import { FC } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button, Select, Spacer, Text } from '@chakra-ui/react';
import { CountryCode } from '@shinetools/geo-library';

import * as Form from 'components/_core/form';

import useCountriesList from '../../../../../../../common/countries/useCountriesList';
import SunshineCard from '../../../../../../../components/_core/SunshineCard';
import SunshineIcon from '../../../../../../../components/_core/SunshineIcon';
import SunshineSquircleIcon from '../../../../../../../components/_core/SunshineSquircleIcon';
import Loader from '../../../../../../../components/Loader';
import { FRANCE_COUNTRY_CODE } from '../../../../../../Bento/libs/constants';
import locales from '../locales';
import { TaxResidenciesSchema } from '../TaxResidencies';

interface TaxResidencyFormProps {
  isLoading: boolean;
  submitCountry: (index: number) => void;
  excludedCountries: CountryCode[];
  index: number;
}

const TaxResidencyForm: FC<TaxResidencyFormProps> = ({
  excludedCountries,
  index,
  isLoading,
  submitCountry,
}) => {
  const {
    formState: { errors },
    register,
    trigger,
    watch,
  } = useFormContext<TaxResidenciesSchema>();

  const taxResidency = watch('taxResidencies')[index];

  const countriesOptions = useCountriesList({
    excludedCountries,
    includeDrom: false,
  }).map(({ emoji, label, value }) => (
    <option key={value} value={value}>
      {`${label} ${emoji}`}
    </option>
  ));

  const { remove } = useFieldArray({
    name: 'taxResidencies',
  });
  const onCountryChange = (country: string) => {
    if (country !== FRANCE_COUNTRY_CODE) {
      submitCountry(index);
    }
  };

  const deleteTaxResidency = () => {
    remove(index);
    trigger();
  };

  return (
    <SunshineCard.Group key="tax-residency" marginBottom="space-24">
      <SunshineCard
        alignItems="center"
        id="header"
        title={index === 0 ? locales.cardTitle : locales.cardTitleSecond}
      >
        <SunshineCard.Slot name="aside">
          {/*TODO: Find correct icon as house does not exists*/}
          <SunshineSquircleIcon name="home" />
        </SunshineCard.Slot>
      </SunshineCard>

      <SunshineCard id="country-selection">
        <>
          <Form.Field label={locales.countryLabel}>
            <Select
              {...register(`taxResidencies.${index}.country`, {
                onChange: (e) => onCountryChange(e.target.value),
                required: true,
              })}
              data-testid="tax-residency-country-input"
              placeholder={locales.countryPlaceholder}
            >
              {countriesOptions}
            </Select>
          </Form.Field>

          {isLoading ? (
            <Loader />
          ) : (
            <>
              {taxResidency?.country &&
              taxResidency.country !== FRANCE_COUNTRY_CODE ? (
                <>
                  <Form.Field
                    error={errors.taxResidencies?.[index]?.fiscalId?.message}
                    label={locales.taxIdentificationNumberLabel}
                    marginTop="space-24"
                  >
                    <Form.Input
                      {...register(`taxResidencies.${index}.fiscalId`)}
                      data-testid="tax-identification-number-input"
                    />
                  </Form.Field>
                  <Text color="grey.600" size="sm">
                    {locales.taxIdentificationNumberDescription}
                  </Text>
                </>
              ) : null}

              <Spacer />

              {index !== 0 ? (
                <Button
                  marginTop="space-24"
                  onClick={deleteTaxResidency}
                  variant="inline-secondary"
                >
                  {locales.deleteButton}
                  <SunshineIcon name="trash" />
                </Button>
              ) : null}
            </>
          )}
        </>
      </SunshineCard>
    </SunshineCard.Group>
  );
};

export default TaxResidencyForm;
